import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React from "react";
import Grid from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { AppState } from '../../../store/rootReducer';
import { IApplication } from "../../../Interfaces/Application/IApplication";
import { IConfiguration, useConfiguration } from "../../../Data/ConfigurationData";
import IndirectQuoterColumn from "../../Quoters/IndirectQuoter/IndirectQuoterColumn/IndirectQuoterColumn";
import {
  IndirectQuoteFieldType,
  IndirectQuoterColumnConfig
} from "../../../Interfaces/IndirectQuoter/IndirectQuoterFieldConfig";
import { usePricingPrograms } from "../../../Data/LookupData";
import {
  getAppQuoteByType,
  getDealerRequestedColumnConfig,
  getPrimaryApplicantIncomeAndDebtInfo,
  getDealerCheckColumnConfig,
  getUnderwriterDecisionColumnConfig,
  getDealerReserve,
  getDealerCheck,
  isAmountFinancedCompliant,
  areAmountFinancedValuesEqual,
  isCarletonAprCompliant,
  isAprCompliant,
} from "../../Quoters/IndirectQuoter/IndirectQuoterUtils";
import {copyQuote, IndirectTransactionType, IQuote} from "../../../Interfaces/Common/IQuote";
import {add, divide, hasValue, multiply} from "@amsanalytics/react-shared-library";
import {
  DealerMiscFee,
} from "../../../Data/IDealertrackDealerDecisionData";
import { Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import styles from "../../Quoters/IndirectQuoter/IndirectQuoterColumn/_indirect-quoter-column.module.scss";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { IndirectQuoteType } from "../../../Data/IndirectQuoteType";
import WarningIcon from "@mui/icons-material/Warning";
import { IRuleViolation } from "../../../Interfaces/Application/IApplicationQuote";
import { showValidationDialog } from "../../../utils/ValidationDialog";
import { Organization } from "@amsanalytics/react-shared-library/build/Models/Organization";
import { IQuotePayType } from "../../../Interfaces/Common/IQuotePayType";
import {
  calculateAmountToFinanceFromPayment,
  calculateIndirectFinancedAmount,
  calculatePayment,
  getAmortizationSchedule,
  getCashDown
} from "../../../utils/quoteUtils";
import {getApplicant} from "../../../utils/appUtils";
import {Constants} from "../../../utils/constants";

export interface IndirectDecisionBoxUnderwriterQuotesProps {
  disableQuotes?: boolean;
  readOnly?: boolean;
  updateApplication: (fn: (app: IApplication) => IApplication | void) => void;
  configuration: IConfiguration;
  isDealerRetailQuote?: boolean;
}

export const IndirectDecisionBoxUnderwriterQuotes = (props: IndirectDecisionBoxUnderwriterQuotesProps) => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const organizations: Organization[] = useSelector((state: AppState) => state.applications.lookups.organizations);
  const { data: pricingPrograms } = usePricingPrograms();
  const payTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  const miscFeeInformationList: DealerMiscFee[] = props.configuration?.miscFeeInformationList ?? [];
  const { data: configuration } = useConfiguration();
  // Whether the user can modify the Dealer Flow Type
  const hideCopyCalculatedQuoteIndirectDecisionBox = configuration?.featureFlags?.[FeatureFlagIds.HideCopyCalculatedQuoteIndirectDecisionBox] ?? false; // These are being fed in the same value for now, but can change in the future
  const hideAllRecommendedValues = configuration?.featureFlags?.[FeatureFlagIds.HideCopyCalculatedQuoteIndirectDecisionBox] ?? false; // These are being fed in the same value for now, but can change in the future
  const organizationForApp = organizations.find(o => o.id === currentApp?.organizationId);

  const handleUpdateQuote = (
    columnConfig: IndirectQuoterColumnConfig,
    fieldName: string,
    value: IndirectQuoteFieldType
  ): void => {
    if (props.readOnly) {
      return;
    }
    if (!currentApp || !columnConfig.editableApplicationQuote) {
      return;
    }

    const isRetailQuote = (quote: IQuote) => quote.transactionType === IndirectTransactionType.Retail;
    const isDealerOrUnderwriterQuote = (quote: IQuote) =>
      quote.quoteType === IndirectQuoteType.DealerRequested || quote.quoteType === IndirectQuoteType.Underwriter;

    const handleRetailAdjustments = (
      quoteToUpdate: IQuote,
      underwriterQuote: IQuote | undefined,
      applicationToUpdate: IApplication
    ): void => {
      if (!isRetailQuote(quoteToUpdate) && !isDealerOrUnderwriterQuote(quoteToUpdate)) {
        return;
      }

      let amountFinanced = calculateIndirectFinancedAmount(quoteToUpdate, miscFeeInformationList);
      quoteToUpdate.amountToFinance = amountFinanced;

      if (fieldName === "tradeAllowance" && quoteToUpdate.quoteType === IndirectQuoteType.Underwriter && currentApp?.appSource !== "Dealer Track") {
        quoteToUpdate.tradeLien = 0;
      }

      if (!isRetailQuote(quoteToUpdate)) {
        return;
      }

      if (amountFinanced) {
        amountFinanced += quoteToUpdate.lenderFees ?? 0;
      }

      if (underwriterQuote) {
        underwriterQuote.amountToFinance = amountFinanced;
        const primary = getApplicant(applicationToUpdate, Constants.roleApplicantId, true);
        if (!primary?.customer?.customerIncomeDebt?.[0]) {
          return;
        }
        primary.customer.customerIncomeDebt[0].downpayment = quoteToUpdate.cashDown ?? primary.customer.customerIncomeDebt[0].downpayment;
        underwriterQuote.cashDown = primary.customer.customerIncomeDebt[0].downpayment;

        if (fieldName === "amountToFinance") {
          // Same misc fee list as the amount financed above, so the two values round-trip
          underwriterQuote.cashDown = getCashDown(underwriterQuote, miscFeeInformationList);
        }
      }
    };

    const handlePaymentCallAdjustments = (quoteToUpdate: IQuote, underwriterQuote: IQuote | undefined): void => {
      if (
        quoteToUpdate.transactionType !== IndirectTransactionType.PaymentCall
        || !configuration?.featureFlags[FeatureFlagIds.AllowRateEdit]
        || !underwriterQuote
        || (fieldName !== "apr" && fieldName !== "payment" && fieldName !== "term" && fieldName !== "quotePayTypeId")
      ) {
        return;
      }

      underwriterQuote.amountToFinance =
        calculateAmountToFinanceFromPayment(underwriterQuote.payment, underwriterQuote, configuration, payTypes) ?? undefined;
    };

    const updateDealerValues = (quoteToUpdate: IQuote): void => {
      quoteToUpdate.dealerPartialPercent = organizationForApp?.reservePercent;
      quoteToUpdate.dealerReserve = getDealerReserve(quoteToUpdate);
      quoteToUpdate.dealerCheck = getDealerCheck(quoteToUpdate);
    };

    const shouldRecalculateRetailPayment = (quoteToUpdate: IQuote): boolean =>
      (isRetailQuote(quoteToUpdate) &&
        fieldName !== "payment" &&
        configuration?.featureFlags?.[FeatureFlagIds.UseBusinessRulePaymentCalculation] !== true) ||
      props.isDealerRetailQuote === true;

    const recalculateRetailPayment = (quoteToUpdate: IQuote): void => {
      if (!shouldRecalculateRetailPayment(quoteToUpdate)) {
        return;
      }

      let newPayment: number | undefined;
      const payType = payTypes.find(qpt => qpt.id === quoteToUpdate.quotePayTypeId);

      if (quoteToUpdate.amountToFinance && quoteToUpdate.apr && quoteToUpdate.term && payType) {
        const financeTerm = quoteToUpdate.term + (quoteToUpdate.balloonTerm ?? 0);
        let additionalMonthlyPayment = 0;

        if (configuration?.featureFlags?.[FeatureFlagIds.Use45DayFirstPayment]) {
          const amortizationSchedule = getAmortizationSchedule(quoteToUpdate);

          if (amortizationSchedule.length > 1) {
            const additionalInterest = multiply(add(amortizationSchedule[0].interest, amortizationSchedule[1].interest), 0.25);
            additionalMonthlyPayment = divide(additionalInterest, financeTerm);
          }

          newPayment = add(
            calculatePayment(quoteToUpdate.apr, payType.paymentsPerYear, financeTerm, quoteToUpdate.amountToFinance),
            additionalMonthlyPayment
          );
        } else {
          newPayment = calculatePayment(quoteToUpdate.apr, payType.paymentsPerYear, financeTerm, quoteToUpdate.amountToFinance);
        }
      }

      if (isRetailQuote(quoteToUpdate) && quoteToUpdate.payment !== newPayment) {
        quoteToUpdate.payment = newPayment;
      }
    };

    const updateFundingCompliance = (app: IApplication, underwriterQuote: IQuote | undefined): void => {
      if (!app.applicationFunding || !underwriterQuote) {
        return;
      }

      app.applicationFunding.amountFinancedCompliant = isAmountFinancedCompliant(underwriterQuote, app.applicationFunding);
      app.applicationFunding.amountFinancedEqualCompliant = areAmountFinancedValuesEqual(app.applicationFunding);
      app.applicationFunding.carletonAprCompliant = isCarletonAprCompliant(app.applicationFunding, props.configuration);
      app.applicationFunding.aprCompliant = isAprCompliant(underwriterQuote.apr, app.applicationFunding.disclosureApr, props.configuration);
    };

    props.updateApplication?.((applicationToUpdate: IApplication): IApplication | undefined => {
      const quoteToUpdate = applicationToUpdate?.applicationQuotes.find(aq => aq.quoteId === columnConfig.editableApplicationQuote!.quoteId)?.quote;
      if (!quoteToUpdate) {
        return undefined;
      }

      const underwriterQuote = getAppQuoteByType(applicationToUpdate, IndirectQuoteType.Underwriter)?.quote;
      quoteToUpdate[fieldName] = value;

      handleRetailAdjustments(quoteToUpdate, underwriterQuote, applicationToUpdate);
      handlePaymentCallAdjustments(quoteToUpdate, underwriterQuote);
      updateDealerValues(quoteToUpdate);
      recalculateRetailPayment(quoteToUpdate);
      updateFundingCompliance(applicationToUpdate, underwriterQuote);

      return applicationToUpdate;
    });
  };

  // Getting the data and column / row formatting
  const dealerRequestedQuoteColumnConfig: IndirectQuoterColumnConfig = getDealerRequestedColumnConfig(currentApp, false);
  const underwriterDecisionedQuoteColumnConfig: IndirectQuoterColumnConfig = getUnderwriterDecisionColumnConfig(
    currentApp, 
    payTypes, 
    hideAllRecommendedValues,
    configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] ?? false,
    configuration?.featureFlags[FeatureFlagIds.AllowTermEdit] ?? false,
    configuration?.featureFlags[FeatureFlagIds.AllowRateEdit] ?? false
  );
  const solutionBoxQuoteColumnConfig: IndirectQuoterColumnConfig = getDealerCheckColumnConfig(
    currentApp,
    pricingPrograms,
    organizations,
    hideCopyCalculatedQuoteIndirectDecisionBox,
    hideAllRecommendedValues,
    configuration?.featureFlags[FeatureFlagIds.AllowCalculatedTierOverride] ?? false,
    configuration?.featureFlags[FeatureFlagIds.UseRetailDealerFlow] ?? false
  );

  // This section will apply padding to every row in each column if one of the rows has an error message 
  // Find the maximum length of the three arrays
  const maxColumnLength = Math.max(
    dealerRequestedQuoteColumnConfig.fieldConfigs.length,
    underwriterDecisionedQuoteColumnConfig.fieldConfigs.length,
    solutionBoxQuoteColumnConfig.fieldConfigs.length
  );

  // Loop through each index position
  for (let i = 0; i < maxColumnLength; i++) {
    // Check if any of the columns at this index have a non-empty error message
    const hasErrorAtIndex = (i < dealerRequestedQuoteColumnConfig.fieldConfigs.length && hasValue(dealerRequestedQuoteColumnConfig.fieldConfigs[i]?.errorMessage))
      || (i < underwriterDecisionedQuoteColumnConfig.fieldConfigs.length && hasValue(underwriterDecisionedQuoteColumnConfig.fieldConfigs[i]?.errorMessage))
      || (i < solutionBoxQuoteColumnConfig.fieldConfigs.length && hasValue(solutionBoxQuoteColumnConfig.fieldConfigs[i]?.errorMessage));

    // If there's an error at this index, update all columns' showErrorSpacing
    if (hasErrorAtIndex) {
      // Update dealerRequestedQuoteColumnConfig.fieldConfigs if index exists
      if (i < dealerRequestedQuoteColumnConfig.fieldConfigs.length) {
        dealerRequestedQuoteColumnConfig.fieldConfigs[i].showErrorSpacing = true;
      }

      // Update underwriterDecisionedQuoteColumnConfig if index exists
      if (i < underwriterDecisionedQuoteColumnConfig.fieldConfigs.length) {
        underwriterDecisionedQuoteColumnConfig.fieldConfigs[i].showErrorSpacing = true;
      }

      // Update solutionBoxQuoteColumnConfig.fieldConfigs if index exists
      if (i < solutionBoxQuoteColumnConfig.fieldConfigs.length) {
        solutionBoxQuoteColumnConfig.fieldConfigs[i].showErrorSpacing = true;
      }
    }
  }

  const useAllRecommendedValues = (): void => {
    props.updateApplication && props.updateApplication((app) => {
      const primaryApplicantIncomeAndDebtInfo = getPrimaryApplicantIncomeAndDebtInfo(currentApp);
      const underwriterAppQuote = getAppQuoteByType(app, IndirectQuoteType.Underwriter);
      const copyFromApplicationQuote = getAppQuoteByType(app, IndirectQuoteType.Recommended);

      if (underwriterAppQuote && copyFromApplicationQuote?.quote) {
        underwriterAppQuote.quote = copyQuote(copyFromApplicationQuote.quote, underwriterAppQuote.quoteId, IndirectQuoteType.Underwriter);
        underwriterAppQuote.useVehiclePrice = copyFromApplicationQuote.useVehiclePrice;
        underwriterAppQuote.quote.cashDown = Math.max(copyFromApplicationQuote.quote.cashDown ?? 0, primaryApplicantIncomeAndDebtInfo?.downpayment ?? 0);

        if (app.applicationFunding) {
          // Update the compliance booleans on the funding object if it exists
          app.applicationFunding.amountFinancedCompliant = isAmountFinancedCompliant(underwriterAppQuote.quote, app.applicationFunding);
          app.applicationFunding.amountFinancedEqualCompliant = areAmountFinancedValuesEqual(app.applicationFunding);
          app.applicationFunding.carletonAprCompliant = isCarletonAprCompliant(app.applicationFunding, props.configuration);
          app.applicationFunding.aprCompliant = isAprCompliant(underwriterAppQuote.quote.apr, app.applicationFunding.disclosureApr, props.configuration);
        }
      }
    });
  }

  return (
    <Grid container spacing={1}>
      {props.readOnly && (
        <div style={{ width: '100%', padding: '8px 12px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '8px', fontSize: '13px', color: '#856404' }}>
          This quoter is read-only after approval.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, opacity: props.readOnly ? 0.7 : 1, pointerEvents: props.readOnly ? 'none' : 'auto' }}>

        {/*Quote from dealer*/}
        {props.disableQuotes !== true && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <IndirectQuoterColumn
              columnConfig={dealerRequestedQuoteColumnConfig}
              showUseRecommendedButton={false}
              onChange={handleUpdateQuote}
            />
          </div>
        )}

        {/*Decisioned quote*/}
        {props.disableQuotes !== true &&
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
            <IndirectQuoterColumn
              columnConfig={underwriterDecisionedQuoteColumnConfig}
              onChange={handleUpdateQuote}
              showUseRecommendedButton={false}
              headerActions={
                props.configuration.isFinanceCompany ? (
                  <>
                    {!hideCopyCalculatedQuoteIndirectDecisionBox && (
                      <RecommendedHeaderActions useAllRecommendedValues={useAllRecommendedValues} />
                    )}
                    <QuoteErrorsIcon ruleViolations={underwriterDecisionedQuoteColumnConfig.editableApplicationQuote?.ruleViolations} />
                  </>
                ) : undefined
              }
            />
          </div>
        }

        {/*Calculated values that are quote-adjacent*/}
        {props.disableQuotes !== true &&
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
            <IndirectQuoterColumn
              columnConfig={solutionBoxQuoteColumnConfig}
              onChange={handleUpdateQuote}
              showUseRecommendedButton={false}
            />
          </div>
        }
      </div>
    </Grid>
  );
};

interface RecommendedHeaderActionsProps {
  useAllRecommendedValues: () => void;
}

const RecommendedHeaderActions = (props: RecommendedHeaderActionsProps) => {
  return (
    <>
      <div className={styles.recommendedLegend}>
        <span className={styles.legendDot}></span>
        <span className={styles.legendText}>Recommended</span>
      </div>
      <Tooltip title={`Copy the values from the 'Calculated' quote to the quote estimator. Note: This is not available for DealerFlow Retail. It is available for Payment Call.`}>
        <IconButton
          onClick={props.useAllRecommendedValues}
          className={styles.useAllButton}
        >
          <SyncAltIcon color={'primary'} fontSize='small' />
        </IconButton>
      </Tooltip>
    </>
  );
}

interface QuoteErrorsIconProps {
  ruleViolations: IRuleViolation[] | undefined;
}

const QuoteErrorsIcon = (props: QuoteErrorsIconProps) => {
  const showWarnings = (warnings: IRuleViolation[]) => {
    showValidationDialog({
      errors: warnings,
      callback: () => { },
      allowBypass: false
    });
  };

  return (
    <>
      {props.ruleViolations && props.ruleViolations.length > 0 &&
        <IconButton
          size='small'
          onClick={() => {
            showWarnings(props.ruleViolations!);
          }}
          onMouseDown={(e) => e.preventDefault()}
          edge='end'
        >
          <WarningIcon color='error' fontSize='small' />
        </IconButton>
      }
    </>
  );
}